import React, { ReactElement, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import useNav from '../../../../hooks/useNav';
import useMixPanel from '../../../../hooks/useMixPanel';
import { EVENT_NAMES, TRACK_PROFILE_VIEWS_FOR_OFFER_MADE } from '../../../../constants/mixpanel';
import { DashboardDiscoverModeMediaCounterProps, ItemProps } from './types/DashboardDiscoverModeMediaCounter';
import DiscoverCounterInfoIcon from '../../../../atoms/icons/DiscoverCounterInfoIcon';
import DashboardDiscoverModeMediaRenderDots from './DashboardDiscoverModeMediaRenderDots';
import { DotsAnimatedWrapper, DotsWrapper } from '../../../common/CommonStyle';
import { animated, useTransition } from '@react-spring/web';
import { useDashboardDiscoverMode } from '../../../../contexts/DashboardDiscoverModeContext';

const MediaCounterContainer = styled('div')({
  background: '#000',
  height: 41,
  display: 'flex',
  justifyContent: 'flex-end',
  width: '100%',
});

const CounterWrapper = styled('div')({
  display: 'inline-block',
  marginRight: 1,
  marginTop: 2,
  width: 'calc(100% - 160px)',
  position: 'relative',
});

const InfoIconWrapper = styled('div')({
  padding: '1px 32px 11px 22px',
  cursor: 'pointer',
});

const DashboardDiscoverModeMediaCounter = ({
  photos,
  currentSlide,
  hashId,
}: DashboardDiscoverModeMediaCounterProps): ReactElement => {
  const { goTo } = useNav();
  const { setMixpanelDataByKeyHandler, trackProfileView } = useMixPanel();
  const { animateDirection, isDotsAnimated, setIsDotsAnimated } = useDashboardDiscoverMode();

  const discoverDotsRef = useRef([]);
  const [ease, setEase] = useState({ x1: 0 });
  const [applyAnimation, setApplyAnimation] = useState(false);

  useEffect(() => {
    return () => {
      setIsDotsAnimated(false);
    };
  }, []);

  useLayoutEffect(() => {
    if (photos.length === 2) {
      const x1 = discoverDotsRef?.current[0]?.offsetLeft;

      setEase({ x1: x1 });
    } else {
      setApplyAnimation(false);
      const nextReft =
        animateDirection === 'left'
          ? discoverDotsRef?.current[currentSlide + 1]
          : discoverDotsRef?.current[currentSlide];
      const currentRef = discoverDotsRef?.current[currentSlide];
      const x1 = animateDirection === 'left' ? nextReft?.offsetLeft : currentRef?.offsetLeft;

      setEase({ x1 });
    }

    setApplyAnimation(!(currentSlide === 0 && !isDotsAnimated));
  }, [photos, currentSlide, animateDirection, isDotsAnimated]);

  const transitions = useTransition(currentSlide, {
    keys: currentSlide,
    from: {
      opacity: 1,
      transform: 'scaleX(1)',
    },
    enter: {
      opacity: 0,
      transform: 'scaleX(0)',
    },
    delay: 300,
  });

  const renderAnimatedDiv = () => {
    let diffX = Math.abs(ease.x1 - 12);

    if (photos.length === 2) {
      diffX = Math.abs(ease.x1);
    }

    return (
      <DotsAnimatedWrapper style={{ display: 'flex' }}>
        {transitions(styles => (
          <animated.div
            style={{
              ...styles,
              position: 'absolute',
              height: 6,
              backgroundColor: 'white',
              borderRadius: '6px 6px 6px 6px',
              visibility: isDotsAnimated && applyAnimation ? 'visible' : 'hidden',
              top: '-11px',
              left: `${diffX + 'px'}`,
              transformOrigin: animateDirection === 'left' ? '0% 100%' : '100% 0%',
              width: '19px',
              transition: 'transform 0.3s ease-out forwards',
            }}
          />
        ))}
      </DotsAnimatedWrapper>
    );
  };

  return (
    <MediaCounterContainer>
      <CounterWrapper className="counter-info-wrapper">
        {photos.length > 0 && (
          <>
            <DotsWrapper className="dots-wrapper">
              {photos.map((item: ItemProps, index: number) => {
                return (
                  <DashboardDiscoverModeMediaRenderDots
                    ref={element => {
                      discoverDotsRef.current[index] = element;
                    }}
                    key={index}
                    item={item}
                    index={index}
                    currentSlide={currentSlide}
                    photos={photos}
                  />
                );
              })}
            </DotsWrapper>
            {renderAnimatedDiv()}
          </>
        )}
      </CounterWrapper>
      <InfoIconWrapper
        onClick={() => {
          goTo(`/profile/${hashId}?origin=dashboard`);
          setMixpanelDataByKeyHandler({
            key: EVENT_NAMES.DISCOVER_MODE_ACTION,
            payload: { 'Profile Viewed': true },
          });
          setMixpanelDataByKeyHandler({
            key: EVENT_NAMES.OFFER_MADE,
            payload: { source: TRACK_PROFILE_VIEWS_FOR_OFFER_MADE.profileViewDiscover },
          });
          trackProfileView({ Source: 'Discover Mode', User: hashId });
        }}
        className="pressable"
      >
        <DiscoverCounterInfoIcon />
      </InfoIconWrapper>
    </MediaCounterContainer>
  );
};

export default DashboardDiscoverModeMediaCounter;
